import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Loader } from "@/components/common/Loader";
import { TextInput } from "@/components/common/Forms";
import ListManager from "@/pages/admin/settings/ListManager";
import { api, errMsg, useFetch } from "@/lib/api";

function ShopSettings({ data, save }) {
  const { data: status } = useFetch("/admin/shopify/status");
  const { data: products } = useFetch("/admin/shop/products");
  const [pid, setPid] = useState(data.featured_product_id || "none");
  const [note, setNote] = useState(data.featured_note || "");
  const current = (products || []).find((p) => p.id === pid);
  return (
    <section className="lux-card p-6 sm:p-8 space-y-4" data-testid="shop-settings">
      <h2 className="text-2xl font-normal">Shopify</h2>
      <p data-testid="shopify-status" className="text-sm">
        {status?.configured ? `Collegato al negozio ${status.shop}: i centri vedono i prodotti che non hanno mai provato.` : "Shopify non ancora collegato."}
      </p>
      <div className="hairline pt-5 space-y-4">
        <h3 className="text-xl font-normal">Prodotto della settimana</h3>
        <p className="text-sm text-warm-text">Compare in evidenza nella home dei centri, con il link diretto al negozio. Non viene mostrato ai centri non abilitati al suo protocollo.</p>
        <div className="flex items-center gap-4">
          {current?.image && <img src={current.image} alt="" className="w-14 h-14 object-cover border border-warm/40" />}
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="featured-product-select" className="bg-ivory-deep rounded-xl h-11 flex-1"><SelectValue placeholder="Scegli un prodotto" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="none">Nessun prodotto in evidenza</SelectItem>
              {(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <TextInput data-testid="featured-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Messaggio facoltativo, es. Perfetto per la stagione estiva" />
        <button data-testid="save-featured-btn" onClick={() => save({ featured_product_id: pid === "none" ? "" : pid, featured_note: note })} className="btn-gold">Salva prodotto in evidenza</button>
      </div>
    </section>
  );
}

function GeneralSettings() {
  const { data, setData } = useFetch("/admin/settings");
  const [privacy, setPrivacy] = useState("");
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (data && !ready) {
      setReady(true);
      setPrivacy(data.privacy_text || "");
      setEmails(data.notification_emails || []);
    }
  }, [data, ready]);
  const save = async (body) => {
    try {
      const r = await api.put("/admin/settings", body);
      setData(r.data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!data) return <Loader />;
  const addEmail = (e) => {
    e.preventDefault();
    if (newEmail.trim() && !emails.includes(newEmail.trim().toLowerCase())) setEmails([...emails, newEmail.trim().toLowerCase()]);
    setNewEmail("");
  };
  return (
    <div className="space-y-6">
      <ShopSettings data={data} save={save} />
      <section className="lux-card p-6 sm:p-8 space-y-4" data-testid="privacy-settings">
        <h2 className="text-2xl font-normal">Testo informativa privacy</h2>
        <p className="text-sm text-warm-text">Mostrato nel modulo di iscrizione. Ogni centro conserva la versione accettata.</p>
        <Textarea data-testid="privacy-text-input" value={privacy} onChange={(e) => setPrivacy(e.target.value)} rows={10} className="bg-ivory-deep rounded-xl" />
        <button data-testid="save-privacy-btn" onClick={() => save({ privacy_text: privacy })} className="btn-gold">Salva testo</button>
      </section>
      <section className="lux-card p-6 sm:p-8 space-y-4" data-testid="notification-settings">
        <h2 className="text-2xl font-normal">Email per le notifiche</h2>
        <p className="text-sm text-warm-text">Ricevono le nuove iscrizioni e i nuovi ticket non assegnati. Se l'elenco è vuoto, avvisiamo tutti gli Admin.</p>
        <ul className="flex flex-wrap gap-2">
          {emails.map((em) => (
            <li key={em} className="inline-flex items-center gap-2 bg-ivory-deep border border-warm/50 px-3 py-1.5 text-sm">
              {em}<button data-testid={`remove-notification-email-${em}`} onClick={() => setEmails(emails.filter((x) => x !== em))} aria-label="Rimuovi"><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
        <form onSubmit={addEmail} className="flex gap-3">
          <TextInput data-testid="notification-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="nome@skinlovers.it" />
          <button className="btn-ghost shrink-0" data-testid="add-notification-email-btn">Aggiungi</button>
        </form>
        <button data-testid="save-notification-emails-btn" onClick={() => save({ notification_emails: emails })} className="btn-gold">Salva indirizzi</button>
      </section>
    </div>
  );
}

const tabCls = "px-0 pb-3 rounded-xl bg-transparent shadow-none data-[state=active]:shadow-none data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-gold text-[12px] uppercase tracking-[0.16em] whitespace-nowrap";

export default function Settings() {
  const { data, reload } = useFetch("/admin/lists");
  return (
    <div>
      <PageHeader eyebrow="Super Admin" title="Impostazioni" />
      <Tabs defaultValue="types">
        <TabsList className="bg-transparent p-0 h-auto gap-6 sm:gap-10 border-b border-warm/40 w-full justify-start rounded-xl overflow-x-auto mb-8">
          <TabsTrigger value="types" data-testid="settings-tab-types" className={tabCls}>Tipologie</TabsTrigger>
          <TabsTrigger value="protocols" data-testid="settings-tab-protocols" className={tabCls}>Protocolli</TabsTrigger>
          <TabsTrigger value="categories" data-testid="settings-tab-categories" className={tabCls}>Categorie ticket</TabsTrigger>
          <TabsTrigger value="general" data-testid="settings-tab-general" className={tabCls}>Privacy, notifiche, Shopify</TabsTrigger>
        </TabsList>
        {!data ? <Loader /> : (
          <>
            <TabsContent value="types"><ListManager kind="client-types" items={data["client-types"]} onChanged={reload} title="Tipologie cliente" hint="Assegnate in fase di approvazione. In futuro ogni tipologia sbloccherà funzioni dedicate." /></TabsContent>
            <TabsContent value="protocols"><ListManager kind="protocols" items={data.protocols} onChanged={reload} title="Protocolli" hint="Proposti nel modulo di iscrizione e usati per gli attestati." /></TabsContent>
            <TabsContent value="categories"><ListManager kind="ticket-categories" items={data["ticket-categories"]} onChanged={reload} title="Categorie ticket" /></TabsContent>
          </>
        )}
        <TabsContent value="general"><GeneralSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
